// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import sendAutocompleteOdpEvent from "@insite/client-framework/Store/CommonHandlers/SendAutocompleteOdpEvent";
import sendSearchTracking from "@insite/client-framework/Store/CommonHandlers/SendSearchTracking";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, AutocompleteModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import AutocompleteBrands, {
    AutocompleteBrandsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteBrands";
import AutocompleteCategories, {
    AutocompleteCategoriesStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteCategories";
import AutocompleteContent, {
    AutocompleteContentStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteContent";
import { FormFieldSizeVariant } from "@insite/mobius/FormField";
import { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import {
    ContentBodyProps,
    OverflowWrapperProps,
    PopoverPresentationProps,
    PositionStyle,
} from "@insite/mobius/Popover";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import FancyScroll, { FancyScrollRefProps } from "@keller/App/Components/Common/FancyScroll";
import xHeaderUpdate from "@keller/App/Handlers/XNG/Header";
import { getSearchHistory, SearchHistoryItem } from "@keller/App/Services/AutocompleteService";
import { CustomAppState } from "@keller/App/Store/Reducers";
import AutocompleteProducts, {
    AutocompleteProductsStyles,
} from "@keller/Overrides/Widgets/Header/AutocompleteProducts";
import SearchHistory, { SearchHistoryStyles } from "@keller/Overrides/Widgets/Header/SearchHistory";
import Search from "@keller/XDL/Icons/Search";
import { color, getHeaderHeight, parseBoolean, styledScrollbar, xScreen } from "@keller/XDL/Utilities";
import { produce } from "immer";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import React, { createRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";
import { startSearch } from "@insite/client-framework/Store/CommonHandlers/StartSearch";
import AutocompleteSuggestions, {
    AutocompleteSuggestionsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteSuggestions";
import { FloatingTooltipStyles } from "@keller/App/Components/FloatingTooltip/FloatingTooltip";
import FloatingMenuTooltip from "@keller/App/Components/Menu/FloatingMenuTooltip";

interface OwnProps {
    /** A unique identifier to assist with id-generation for `aria-controls`. */
    readonly id: string;
    inputRef?: React.Ref<HTMLInputElement>;
    autocompletePositionFunction?: (element: React.RefObject<HTMLUListElement | null>) => PositionStyle;
    onBeforeGoToUrl?: () => void;
    extendedStyles?: SearchInputStyles;
    isAnimated?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const {
        searchSettings: { autocompleteEnabled, searchHistoryEnabled, searchHistoryLimit, searchPath },
    } = getSettingsCollection(state);
    return {
        autocompleteEnabled,
        searchHistoryEnabled,
        searchHistoryLimit,
        location: getLocation(state),
        searchPath,
        isTabletScreen: state.xCommon.isTabletScreen,
        // XNG-Changes
        isMobile: state.xCommon.isMobileScreen,
        query: state.xHeader.searchQuery,
    };
};

const mapDispatchToProps = {
    getAutocompleteModel,
    sendSearchTracking,
    sendAutocompleteOdpEvent,
    startSearch,
    // XNG-Changes
    xHeaderUpdate,
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

interface State {
    /**
     * XNG-Changes: use redux instead of state to make the query sharing with sticky header
     */
    // query: string;
    focusableAutocompleteItems: AutocompleteItemModel[];
    focusedAutocompleteItem?: AutocompleteItemModel;
    focusedSearchHistoryQuery?: string;
    isLoading: boolean;
    // XNG-Changes
    // autoCompleteModel?: AutocompleteModel | null;
    autoCompleteModel?: (AutocompleteModel & { productContexts: ProductContextModel[] }) | null;
    autoCompleteOpen?: boolean;
    clickedProduct: boolean;
}

// XNG-Changes
const DOWN_KEY = "ArrowDown";
const UP_KEY = "ArrowUp";
const ENTER_KEY = "Enter";

const sharedSearchInputStyle: { autocompleteCategoriesStyles?: AutocompleteCategoriesStyles } = {
    autocompleteCategoriesStyles: {
        headerText: {
            size: 14,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 19px;
                padding: 12px 20px;
                background: ${color("common.ui17")};
                margin: 0 0 14px 0;
                max-width: 100%;

                & ~ a,
                & ~ button,
                & ~ ${TypographyStyle}:not(.heading) {
                    margin-bottom: 12px;
                    padding: 0 20px;
                    &:last-child {
                        margin-bottom: 0;
                    }
                }
                display: flex;
                justify-content: space-between;
            `,
        },
        link: {
            color: "text.main",
            typographyProps: {
                size: 15,
                weight: "normal",
                css: css`
                    letter-spacing: 0;
                    line-height: 24px;
                `,
            },
            css: css`
                align-self: baseline;
                width: auto;
                max-width: 100%;
            `,
        },
        focusedCategoryItemText: {
            size: 15,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 24px;
            `,
        },
        focusedLink: {
            color: "text.main",
            typographyProps: {
                size: 15,
                weight: "bold",
                css: css`
                    letter-spacing: 0;
                    line-height: 24px;
                `,
            },
            css: css`
                align-self: baseline;
                width: auto;
                max-width: 100%;
            `,
        },
        categoryItemText: {
            size: 15,
            weight: "normal",
            css: css`
                letter-spacing: 0;
                line-height: 24px;
                width: auto;
                align-self: baseline;
                max-width: 100%;
            `,
        },
    },
};

export interface SearchInputStyles {
    input?: TextFieldPresentationProps;
    popover?: PopoverPresentationProps;
    popoverWrapper?: OverflowWrapperProps;
    popoverContentBody?: ContentBodyProps;
    autocompleteWrapper?: InjectableCss;
    autocompleteColumnWrapper?: InjectableCss;
    autocompleteCommerceSearchWrapper?: InjectableCss;
    searchHistoryStyles?: SearchHistoryStyles;
    autocompleteSuggestionsStyles?: AutocompleteSuggestionsStyles;
    autocompleteCategoriesStyles?: AutocompleteCategoriesStyles;
    autocompleteBrandsStyles?: AutocompleteBrandsStyles;
    autocompleteContentStyles?: AutocompleteContentStyles;
    autocompleteProductsStyles?: AutocompleteProductsStyles;
    // XNG-Changes
    autocompleteColumnProductWrapper?: InjectableCss;
    inputWrapper?: InjectableCss;
    floatingTooltip?: FloatingTooltipStyles;
    spinner?: LoadingSpinnerProps;
}

export const searchInputStyles: SearchInputStyles = {
    input: {
        sizeVariant: "default",
        iconProps: { src: Search, color: "common.ui7" },
    },
    popover: {
        transitionDuration: "regular",
        toggle: false,
    },
    popoverWrapper: {
        as: "div",
        _height: "auto",
        css: css`
            display: inline-flex;
            justify-content: flex-end;
        `,
    },
    popoverContentBody: {
        as: "div",
        _height: "100%",
    },
    autocompleteWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            ${xScreen("lte", "sm")} {
                flex-direction: column-reverse;
            }
        `,
    },
    autocompleteColumnWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 400px;
            min-width: 400px;
            min-height: 400px;
            max-height: 540px;
            padding-bottom: 20px;
            position: relative;
            &:after {
                content: "";
                background: ${color("common.ui6")};
                width: 1px;
                height: 100%;
                position: absolute;
                right: 0;
                top: 0;
            }
            ${xScreen("lte", "sm")} {
                width: 100%;
                min-width: initial;
                &:after {
                    display: none;
                }
            }
            ${xScreen("gt", "sm")} {
                ${styledScrollbar(4)}
            }
        `,
    },
    autocompleteCommerceSearchWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 0 20px 15px 20px;
        `,
    },
    autocompleteColumnProductWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 560px;
            padding: 20px 20px 0;
            max-height: 540px;
            ${xScreen("lte", "md")} {
                width: 100%;
            }
            ${xScreen("lte", "sm")} {
                width: 100%;
                max-height: initial;
            }
            ${xScreen("gt", "sm")} {
                ${styledScrollbar(4)}
            }
        `,
    },
    autocompleteCategoriesStyles: {
        headerText: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.headerText,
        },
        link: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.link,
        },
        focusedCategoryItemText: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.focusedCategoryItemText,
        },
        focusedLink: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.focusedLink,
        },
        categoryItemText: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.categoryItemText,
        },
    },
    autocompleteBrandsStyles: {
        headerText: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.headerText,
        },
        link: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.link,
        },
        focusedBrandItemText: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.focusedCategoryItemText,
        },
        focusedLink: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.focusedLink,
        },
        brandItemText: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.categoryItemText,
        },
    },
    autocompleteContentStyles: {
        headerText: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.headerText,
        },
        link: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.link,
        },
        focusedLink: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.focusedLink,
        },
    },
    inputWrapper: {
        css: css`
            line-height: 0; // to fix extra spacing on firefox
            position: relative;
        `,
    },
    spinner: {
        css: css`
            position: absolute;
            top: 0;
            right: 0;
            margin: 10px;
        `,
    },
};

const styles = searchInputStyles;

class SearchInput extends React.Component<Props, State> {
    private readonly styles: SearchInputStyles;

    // XNG-Changes
    private parentMenu = React.createRef<HTMLUListElement>();
    private inputWrapperRef = createRef<HTMLDivElement>();
    private fancyScrollRef = createRef<FancyScrollRefProps>();

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
        // XNG-Changes
        // const parsedQuery = parseQueryString<{ query?: string }>(this.props.location.search);

        this.state = {
            // XNG-Changes
            // query: parsedQuery.query || "",
            focusableAutocompleteItems: [],
            isLoading: false,
            autoCompleteOpen: false,
            clickedProduct: true,
        };
    }

    list = React.createRef<HTMLUListElement>();
    popover = React.createRef<any>();
    showAutocomplete = true;

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            // clear the search box on navigate away from search
            if (!this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // TODO ISC-12679 - Redesign so that this can be moved to a field of the class rather than state.
                // this.setState({ query: "" });
                this.props.xHeaderUpdate({ key: "searchQuery", value: "" });

                // <XNG-Changes>
                // OOTB issue, when a user do search and return back to home page, the history not showAutocomplete update so the history popup not appear
                this.showAutocomplete = true;
                // </XNG-Changes>
            } else {
                this.closeAutocomplete();
            }
        }
        if (
            this.state.autoCompleteModel &&
            prevState.autoCompleteModel &&
            this.state.autoCompleteModel !== prevState.autoCompleteModel
        ) {
            this.updateFocusableItems();
        }
    }

    componentDidMount() {
        window.addEventListener("resize", this.handleResize, true);
        // XNG-Changes
        const parsedQuery = parseQueryString<{ query?: string }>(this.props.location.search);
        if (parsedQuery.query) {
            this.props.xHeaderUpdate({ key: "searchQuery", value: parsedQuery.query });
        }
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }

    handleResize = () => {
        this.updateFocusableItems();
    };

    updateFocusableItems = () => {
        if (this.state.isLoading || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const isProductsOnTop = window.innerWidth < this.props.theme.breakpoints.values[3];
        const newFocusableOptions = isProductsOnTop
            ? (products || ([] as AutocompleteItemModel[]))
                  .concat(categories || [])
                  .concat(brands || [])
                  .concat(content || [])
            : (categories || [])
                  .concat(brands || [])
                  .concat(content || [])
                  .concat(products || []);
        this.setState({
            focusableAutocompleteItems: newFocusableOptions,
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    doSearch = () => {
        // XNG-Changes
        const query = this.props.query;
        if (query.trim() === "") {
            return;
        }

        // XNG-Changes
        const productUrl = this.state.autoCompleteModel?.products?.[0]?.url;
        let option: string | undefined;
        if (productUrl?.indexOf("option") !== -1 && productUrl?.indexOf("?") !== -1) {
            const query = productUrl?.split("?")?.[1] || "";
            option = parseQueryString<{ option?: string }>(query)?.option;
        }

        this.props.startSearch({
            query,
            autocompleteModel: this.state.autoCompleteModel || undefined,
            searchPath: this.props.searchPath,
            goToUrl: this.goToUrl,
            searchHistoryEnabled: this.props.searchHistoryEnabled,
            searchHistoryLimit: this.props.searchHistoryLimit,
            // XNG-Changes
            option,
        } as any);
    };

    // XNG-Changes: fix empty popup
    clearSearchQuery = () => {
        this.showAutocomplete = true;
        // this.setState({ query: "" });
        this.props.xHeaderUpdate({ key: "searchQuery", value: "" });
    };

    loadAutocomplete = debounce((query: string) => {
        this.setState({ isLoading: true });
        const that = this;
        this.props.getAutocompleteModel({
            query,
            onSuccess: result => {
                this.setState({ isLoading: false });
                if (this.showAutocomplete) {
                    this.setState(
                        {
                            autoCompleteModel: {
                                ...result,
                                productContexts: (result?.products || []).map(product => ({
                                    product: {
                                        id: product.id!,
                                        productNumber: product.erpNumber,
                                        mediumImagePath: product.image,
                                        productTitle: product.title,
                                        canonicalUrl: product.url,
                                        uri: product.uri,
                                        canAddToCart: parseBoolean(product.properties.canAddToCart),
                                        manufacturerItem: product.manufacturerItemNumber,
                                        minimumOrderQty: parseInt(product.properties.minimumOrderQty || "1", 10),
                                        brand: {
                                            name: product.brandName,
                                            detailPagePath: product.brandDetailPagePath,
                                        },
                                        customerProductNumber: product.isNameCustomerOverride ? product.name : "",
                                        properties: {
                                            ...product.properties,
                                        },
                                    } as ProductModel,
                                    productInfo: {
                                        productId: product.id!,
                                        productDetailPath: product.url,
                                        qtyOrdered: parseInt(product.properties.minimumOrderQty || "1", 10),
                                        unitOfMeasure: "",
                                    },
                                    onQtyOrderedChanged: (qty: number) => {
                                        const autoCompleteModel = produce(
                                            this.state.autoCompleteModel,
                                            draftedState => {
                                                if (!draftedState) {
                                                    return;
                                                }
                                                const productIndex = draftedState.productContexts.findIndex(
                                                    p => p.product.id === product.id,
                                                );
                                                draftedState.productContexts[productIndex].productInfo.qtyOrdered = qty;
                                            },
                                        );
                                        this.setState({ autoCompleteModel });
                                    },
                                })),
                            },
                        },
                        () => {
                            this.fancyScrollRef?.current?.updateScrollable();
                        },
                    );
                    // <XNG-Changes>
                    this.updatePopoverVisibility(result);
                    // </XNG-Changes>
                    // this.openAutocomplete();
                }
            },
            onComplete(resultProps) {
                that.setState({ isLoading: false });
                if (resultProps.apiResult) {
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });
    }, 200);

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, toggleAutoComplete = true) => {
        this.showAutocomplete = toggleAutoComplete;
        const newQuery = event.target.value;
        // XNG-Changes
        //  this.setState({ query: newQuery, autoCompleteModel: null }, () => {
        //     this.loadAutocomplete(newQuery);
        // });
        this.setState({ autoCompleteModel: null }, () => {
            this.loadAutocomplete(newQuery);
        });
        this.props.xHeaderUpdate({ key: "searchQuery", value: newQuery });

        // XNG-Changes
        this.updatePopoverVisibility(this.state.autoCompleteModel);
    };

    openAutocomplete = () => {
        // XNG-Changes
        this.setState({ autoCompleteOpen: true });
    };

    closeAutocomplete = () => {
        // XNG-Changes
        this.setState({ autoCompleteOpen: false });
    };

    // XNG-Changes: Check if popeover is opened
    isPopoverVisible(results: AutocompleteModel | undefined | null) {
        const hasSearchQuery = this.props.query?.length > 0;
        const queryLength = this.props.query?.length > 2;
        const hasResults =
            results &&
            ((results.categories && results.categories.length > 0) ||
                (results.brands && results.brands.length > 0) ||
                (results.content && results.content.length > 0) ||
                (results.products && results.products.length > 0));

        if (!hasSearchQuery) {
            const searchHistoryItems = getSearchHistory();
            return this.props.searchHistoryEnabled && searchHistoryItems?.length > 0;
        }

        return this.props.autocompleteEnabled && (hasResults || !queryLength);
    }

    // XNG-Changes
    updatePopoverVisibility(results: AutocompleteModel | undefined | null) {
        if (this.isPopoverVisible(results)) {
            this.openAutocomplete();
        } else {
            this.closeAutocomplete();
        }
    }

    // XNG-Changes
    handleInputFocus = () => {
        /**
         * XNG-Changes
         * KSC-1179: add this.showAutocomplete=true
         */
        this.showAutocomplete = true;
        this.updatePopoverVisibility(this.state.autoCompleteModel);
    };

    // XNG-Changes
    clearSearchHistoryCallback = () => {
        this.updatePopoverVisibility(undefined);
    };

    getPopoverPosition = (element: React.RefObject<HTMLUListElement | null>) => {
        if (this.props.autocompletePositionFunction) {
            return this.props.autocompletePositionFunction(element);
        }

        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        if (element.current) {
            const rect = element.current.getBoundingClientRect();
            positionStyle.top =
                rect.top +
                (rect.height > 100
                    ? sizeVariantValues[this.styles?.input?.sizeVariant || ("default" as FormFieldSizeVariant)].height
                    : rect.height);
            positionStyle.right = document.documentElement.clientWidth - rect.right;

            if (this.props.isAnimated) {
                // Adjust for compact header usage
                positionStyle.top += 3;
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderRight) {
                    positionStyle.right -= Number(this.props.theme.header.borderThickness);
                }
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderTop) {
                    positionStyle.top -= Number(this.props.theme.header.borderThickness);
                }
            }
        }

        return positionStyle;
    };

    moveFocus = throttle((direction: "prev" | "next") => {
        if (this.props.query) {
            this.setState(({ focusedAutocompleteItem, focusableAutocompleteItems }) => {
                const focusableQueue =
                    direction === "prev" ? focusableAutocompleteItems.slice().reverse() : focusableAutocompleteItems;
                const focusedIndex = focusableQueue.indexOf(focusedAutocompleteItem as AutocompleteItemModel);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedAutocompleteItem: newFocusedOption };
            });
        } else {
            this.setState(({ focusedSearchHistoryQuery }) => {
                const searchHistoryItems = getSearchHistory();
                const focusableQueue = direction === "prev" ? searchHistoryItems.slice().reverse() : searchHistoryItems;
                const focusedSearchHistoryItem = focusableQueue.find(o => o.query === focusedSearchHistoryQuery);
                const focusedIndex = focusableQueue.indexOf(focusedSearchHistoryItem as SearchHistoryItem);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedSearchHistoryQuery: newFocusedOption?.query };
            });
        }
    }, 100);

    handlePopoverKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        switch (event.key) {
            case DOWN_KEY:
                this.moveFocus("next");
                break;
            case UP_KEY:
                this.moveFocus("prev");
                break;
            case ENTER_KEY:
                if (this.state.autoCompleteModel && this.state.autoCompleteModel?.productContexts?.length > 0) {
                    if (this.state.focusedAutocompleteItem) {
                        // XNG-Changes
                        (event.target as any).blur();
                        this.goToUrl(this.state.focusedAutocompleteItem.url);
                    } else if (this.state.focusedSearchHistoryQuery) {
                        // XNG-Changes
                        (event.target as any).blur();
                        this.doSearch();
                    } else {
                        // XNG-Changes
                        (event.target as any).blur();
                        this.doSearch();
                    }
                    break;
                } else {
                    break;
                }
            default:
                break;
        }
    };

    handlePopoverClose = () => {
        if (!this.state.clickedProduct && (this.state.autoCompleteModel?.products?.length ?? 0) > 0) {
            this.props.sendAutocompleteOdpEvent({});
        }

        this.setState({
            clickedProduct: false,
        });

        this.setState({
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    trackAutcompleteSearchResults = () => {
        // Make sure only to trigger for autocomplete results
        if (!this.props.query || !this.props.autocompleteEnabled || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const resultCount =
            (products?.length ?? 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);
        if (resultCount > 0) {
            // Track an autocomplete of 1+ items
            this.props.sendSearchTracking({
                searchEvent: "autocompleteSearchResults",
                searchTerm: this.props.query,
                resultCount,
                productCount: products?.length,
                categoryCount: categories?.length,
                contentCount: content?.length,
                brandCount: brands?.length,
            });
            return;
        }

        // Track zero resultCount search results and triggers from this.doSearch
        this.props.sendSearchTracking({
            searchEvent: "searchResults",
            searchTerm: this.props.query,
            resultCount,
        });
    };

    goToUrl = (url: string, track = true) => {
        const data = {
            // XNG-Changes
            // searchQuery: this.state.query,
            searchQuery: this.props.query,
            searchPosition:
                this.state.autoCompleteModel?.suggestions?.findIndex(o => o.url === url) ??
                this.state.autoCompleteModel?.products?.findIndex(o => o.url === url) ??
                -1,
            attributionToken: this.state.autoCompleteModel?.attributionToken,
        };

        if (data.searchPosition >= 0) {
            this.props.sendAutocompleteOdpEvent(data);
            this.setState({ clickedProduct: true });
        }

        this.props.onBeforeGoToUrl && this.props.onBeforeGoToUrl();
        if (track) {
            this.trackAutcompleteSearchResults();
        }
        this.closeAutocomplete();
        this.setState({
            // don't use state results on next search and go to wrong PDP
            autoCompleteModel: undefined,
        });

        // XNG-Changes
        this.props.xHeaderUpdate({ key: "searchQuery", value: "" });
        this.props.history.push(url);
    };

    renderAutocomplete = () => {
        if (!this.props.autocompleteEnabled) {
            return null;
        }

        if (!this.state.autoCompleteModel) {
            return null;
        }

        const {
            categories,
            brands,
            content,
            // XNG-Changes
            // products,
            productContexts,
            suggestions,
            popularBrands,
            popularCategories,
            isRetailSearchCompletionResults,
        } = this.state.autoCompleteModel;

        return (
            <>
                {isRetailSearchCompletionResults &&
                    ((suggestions && suggestions.length > 0) ||
                        (categories && categories.length > 0) ||
                        (popularCategories && popularCategories.length > 0) ||
                        (brands && brands.length > 0) ||
                        (popularBrands && popularBrands.length > 0)) && (
                        <StyledWrapper
                            {...this.styles.autocompleteCommerceSearchWrapper}
                            className="autocomplete-commerce-search-wrapper"
                        >
                            {suggestions && suggestions.length > 0 && (
                                <AutocompleteSuggestions
                                    suggestions={suggestions}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteSuggestionsStyles}
                                />
                            )}
                            {((categories && categories.length > 0) ||
                                (popularCategories && popularCategories.length > 0)) && (
                                <AutocompleteCategories
                                    categories={categories}
                                    popularCategories={popularCategories}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteCategoriesStyles}
                                    isRetailSearchCompletionResults={isRetailSearchCompletionResults}
                                />
                            )}
                            {((brands && brands.length > 0) || (popularBrands && popularBrands.length > 0)) && (
                                <AutocompleteBrands
                                    brands={brands}
                                    popularBrands={popularBrands}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteBrandsStyles}
                                    isRetailSearchCompletionResults={isRetailSearchCompletionResults}
                                />
                            )}
                        </StyledWrapper>
                    )}
                {!isRetailSearchCompletionResults && (
                    <StyledWrapper {...this.styles.autocompleteWrapper} className="autocomplete-wrapper">
                        {((categories && categories.length > 0) ||
                            (brands && brands.length > 0) ||
                            (content && content.length > 0)) && (
                            <StyledWrapper
                                {...this.styles.autocompleteColumnWrapper}
                                className="autocomplete-column-wrapper"
                            >
                                <AutocompleteCategories
                                    categories={categories}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteCategoriesStyles}
                                />
                                <AutocompleteBrands
                                    brands={brands}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteBrandsStyles}
                                />
                                <AutocompleteContent
                                    content={content}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteContentStyles}
                                />
                            </StyledWrapper>
                        )}
                        {productContexts && productContexts.length > 0 && (
                            <FancyScroll refElement={this.parentMenu} ref={this.fancyScrollRef}>
                                <StyledWrapper
                                    {...this.styles.autocompleteColumnProductWrapper}
                                    className="autocomplete-column-wrapper"
                                    ref={this.parentMenu as any}
                                >
                                    <AutocompleteProducts
                                        query={this.props.query}
                                        products={productContexts}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        doSearch={this.doSearch}
                                        extendedStyles={this.styles.autocompleteProductsStyles}
                                    />
                                </StyledWrapper>
                            </FancyScroll>
                        )}
                    </StyledWrapper>
                )}
            </>
        );
    };

    // XNG-Changes
    renderSearchAutoCompleteContent = () => {
        if (!this.props.query && this.showAutocomplete) {
            return (
                <SearchHistory
                    focusedQuery={this.state.focusedSearchHistoryQuery}
                    goToUrl={this.goToUrl}
                    clearSearchHistoryCallback={this.clearSearchHistoryCallback}
                    extendedStyles={{
                        wrapper: {
                            css: css`
                                padding: 20px;
                                width: ${this.inputWrapperRef.current?.getBoundingClientRect().width || 300}px;
                                ${xScreen("lte", "xs")} {
                                    width: 100%;
                                }
                            `,
                        },
                    }}
                />
            );
        }
        if (this.props.query) {
            return this.renderAutocomplete();
        }
    };

    render() {
        const { styles } = this;
        const searchId = `headerSearch-${this.props.id}`;
        // XNG-Changes
        const { isMobile, isTabletScreen } = this.props;

        const searchInput = (
            <StyledWrapper ref={this.inputWrapperRef} {...styles.inputWrapper}>
                <TextField
                    {...styles.input}
                    iconProps={{
                        src: this.state.isLoading ? undefined : Search,
                        color: "common.ui7",
                        size: 20,
                    }}
                    ref={this.props.inputRef}
                    isAnimated={this.props.isAnimated}
                    iconClickableProps={{ onClick: this.doSearch }}
                    value={this.props.query}
                    onChange={this.handleInputChange}
                    onKeyDown={this.handlePopoverKeyDown}
                    onFocus={this.handleInputFocus}
                    placeholder={translate("Search by Keyword or Product Number")}
                    autoComplete="off"
                    clickableText={translate("submit search")}
                    id={searchId}
                    aria-multiline="false"
                    aria-required="false"
                    data-test-selector="headerSearchInputTextField"
                    className="search-input"
                    maxLength={100}
                />
                {this.state.isLoading && <LoadingSpinner {...styles.spinner} size={20} />}
            </StyledWrapper>
        );

        return (
            <>
                <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                    {translate("Site Search")}
                </VisuallyHidden>
                <FloatingMenuTooltip
                    trigger="manual"
                    isOpen={this.state.autoCompleteOpen}
                    triggerComponent={searchInput}
                    overlay={true}
                    showArrow={false}
                    placement="bottom-start"
                    offset={isTabletScreen ? { mainAxis: 5 } : 0}
                    shift={isTabletScreen && !isMobile ? { padding: 15 } : { padding: 0 }}
                    extendedStyles={styles.floatingTooltip}
                    onOpenChange={open => {
                        this.setState({ autoCompleteOpen: open });
                        if (open) {
                            this.fancyScrollRef?.current?.updateScrollable();
                        }
                    }}
                >
                    {this.renderSearchAutoCompleteContent()}
                </FloatingMenuTooltip>
            </>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(SearchInput)));
